import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home } from "lucide-react";

interface TradeErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "user-not-found" | "hash-not-received" | "withdrawal-failed" | "trade-failed";
  email?: string;
  transactionHash?: string;
  error?: string;
}

export function TradeErrorDialog({
  open,
  onOpenChange,
  type,
  email,
  transactionHash,
  error,
}: TradeErrorDialogProps) {
  const navigate = useNavigate();

  const getErrorContent = () => {
    switch (type) {
      case "user-not-found":
        return {
          title: "User Not Found",
          description: `The user account "${email}" could not be found in the system. Please verify the email address and try again.`,
          icon: AlertTriangle,
        };
      case "hash-not-received":
        return {
          title: "Transaction Pending",
          description:
            "Your withdrawal was submitted to the blockchain. The transaction may still be processing and confirming.",
          subtext: transactionHash
            ? `Transaction Hash: ${transactionHash.slice(0, 8)}...${transactionHash.slice(-8)}\n\nPlease wait a few moments and check your wallet. You can view this transaction on Solana Explorer.`
            : "Please check your wallet for pending transactions and try checking back in a few moments.",
          icon: AlertTriangle,
        };
      case "withdrawal-failed":
        return {
          title: "Withdrawal Failed",
          description: error || "Your withdrawal request could not be processed at this time.",
          subtext: "Please try again or contact support if the issue persists.",
          icon: AlertTriangle,
        };
      case "trade-failed":
      default:
        return {
          title: "Trade Failed",
          description: error || "Your trade could not be executed at this time.",
          subtext: "Please review your inputs and try again.",
          icon: AlertTriangle,
        };
    }
  };

  const content = getErrorContent();
  const IconComponent = content.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <IconComponent className="w-6 h-6 text-destructive" />
            <DialogTitle>{content.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        {content.subtext && (
          <div className="bg-muted rounded-lg p-3 text-xs text-muted-foreground">
            {content.subtext}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Try Again
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate("/dashboard");
            }}
            className="flex-1"
          >
            <Home className="w-4 h-4 mr-2" />
            Go Home
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
